import React, { useEffect, useState } from 'react';
import { useClickAway } from 'use-click-away';
import { Link } from '@tcc/crate';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getMessage } from 'util/locale';
import { Popover } from 'react-bootstrap';
import ExportSendToDrivePopover from '../common/ExportSendToDrivePopover';
import { requestPreferredDevice } from 'devices/requesters';
import { sendToAffiliateForExportType } from 'affiliates';
import { downloadScriptsForExportType } from 'scripts';
import { saveSelectedDevice } from 'devices';
import { getPreferredDeviceForScriptType } from 'devices/selectors';
import { getExportOptionsIsOpen,
  bulkExportOptionsIsOpen as getBulkExportOptionsIsOpen,
  scriptCreatorExportOptionsIsOpen as getScriptCreatorExportOptionsIsOpen,
  getActiveScriptType
} from 'current/selectors';
import {
  setExportOptionsIsOpen,
  openPreferredDeviceModal,
  setSendToDriveIsOpen,
  showCreateJDWorkPlanModal
} from 'current/uiwidgets';
import { affiliateIds } from 'constants/affiliates';
import { exportActionTypes, optionSelected } from 'constants/exportSelector';
import { exportTypes } from 'constants/values';
import { scriptTypes } from 'constants/scripts';
import { setExportParams } from 'export-selector';
import { hasAffiliateById } from 'affiliates/selectors';
import type { Drive } from 'store/types/types';
import { staraShapefileId } from 'constants/devices';

const ExportOptionsMenuPopover = ({ exportType }: { exportType: 'bulk' | 'single' }) => {
  const dispatch = useAppDispatch();
  const clickRef = React.useRef('');
  useClickAway(clickRef, () => {
    dispatch(setExportOptionsIsOpen());
    dispatch(setSendToDriveIsOpen(false));
  });
  useEffect(() => { dispatch(requestPreferredDevice()); });
  const [isSendToDriveOpen, setIsSendToDriveOpen] = useState(false);

  const isExportOptionsOpen: boolean = useAppSelector(state => getExportOptionsIsOpen(state, exportType));
  const preferredDevice = useAppSelector(getPreferredDeviceForScriptType);
  const driveDevices = useAppSelector(state => state.devices?.drive) || [];
  const currentScriptId = useAppSelector(state => state.current?.scriptId);
  const hasMyJD = useAppSelector(state => hasAffiliateById(state, affiliateIds.MYJD));
  const isBeaconEnabled = useAppSelector(state => Boolean(state.launchDarkly?.scriptCreatorBeacon));
  const scriptType = useAppSelector(getActiveScriptType);
  // According to Marvin this feature could be released by the end of 2025
  const hasWebDrive2Beta = useAppSelector(state => state.launchDarkly.webDrive2Beta);
  const bulkExportOptionsIsOpen = useAppSelector(getBulkExportOptionsIsOpen);
  const scriptCreatorExportOptionsIsOpen = useAppSelector(getScriptCreatorExportOptionsIsOpen);

  const hasAccessToCreateJDWorkPlan = isBeaconEnabled && hasMyJD && scriptType === scriptTypes.PLANTING;

  const onClickExportToJD = () => {
    dispatch(sendToAffiliateForExportType({ exportType, affiliateId: 'myjd', scriptId: currentScriptId }));
  };
  const onClickSendToDrive = () => {
    setIsSendToDriveOpen(true);
  };
  const onClickCreateWorkPlan = () => {
    dispatch(setExportParams({ params: {} }));
    dispatch(showCreateJDWorkPlanModal());
  };
  const onClickDeviceOption = (device: Drive) => {
    setIsSendToDriveOpen(false);
    dispatch(saveSelectedDevice(device));
    dispatch(setExportOptionsIsOpen({ exportType: exportTypes.SINGLE, status: true }));
    dispatch(openPreferredDeviceModal(exportType, optionSelected));
  };
  const onClickDownloadTrigger = () => {
    const isSingleExport = exportType === exportTypes.SINGLE;
    const isStaraDevice = preferredDevice?.value === staraShapefileId;
    const isPlantingScript = scriptType === scriptTypes.PLANTING;
    if (isSingleExport && (!isStaraDevice || (isStaraDevice && !isPlantingScript))) {
      dispatch(downloadScriptsForExportType({ exportType, scriptId: currentScriptId }));
    } else {
      dispatch(openPreferredDeviceModal(exportType));
    }
  };
  const onClickUpdatePreferredDeviceTrigger = () => {
    dispatch(openPreferredDeviceModal(exportType));
  };

  const placement = bulkExportOptionsIsOpen || scriptCreatorExportOptionsIsOpen ? 'bottom' : 'right';

  return isExportOptionsOpen ? (
    <Popover id='export-options' placement={placement}>
      <ul className='vra-export-options-menu vra-popover-sub-menu'>
        {hasAccessToCreateJDWorkPlan
        && <li>
          <Link
            onClick={onClickCreateWorkPlan}
            data-test-id={`start-${exportActionTypes.CREATE_WORK_PLAN}-single-script`}>
            {getMessage('button-text__create-JD-work-plan')}
            <span className='vra-export-options-menu-john-deere-option'>{getMessage('john-deere-ops-center')}</span>
          </Link>
        </li>}
        <li>
          <Link onClick={onClickExportToJD}>
            {getMessage('button-text__export--myjd', { connected: hasMyJD })}
            <span className='vra-export-options-menu-john-deere-option'>{getMessage('john-deere-ops-center')}</span>
          </Link>
        </li>
        {hasWebDrive2Beta
          && <li>
            <Link onClick={onClickSendToDrive}>
              {getMessage('button-text__send-to-drive')}
            </Link>
            <ExportSendToDrivePopover
              driveDevices={driveDevices}
              isSendToDriveOpen={isSendToDriveOpen}
              onClickDeviceOption={onClickDeviceOption}
            />
          </li>}
        <li>
          <Link onClick={onClickDownloadTrigger}>
            {getMessage('button-text__export--download')}
          </Link>
          <div className={'vra-export-options-prefered-device'}>
            <span className='vra-export-options-menu-device-selected'>
              {preferredDevice?.label || getMessage('text__no-device')}
            </span>
            <Link onClick={onClickUpdatePreferredDeviceTrigger}>
              {getMessage('button-text__change')}
            </Link>
          </div>
        </li>
        <li>
          <div className={'vra-popover-menu__item'}>
            <p className={'vra-popover-menu__item--message'}>
              {getMessage('text__fieldview-export')}
            </p>
          </div>
        </li>
      </ul>
    </Popover >
  ) : null;
};

export default ExportOptionsMenuPopover;
